import { Injectable } from '@angular/core';
import {
  TREQUEST_ORDER_CHECKLIST_LISTING,
  TFilterSelected,
  TREQUEST_SUBMIT_ORDER_CHECKLIST,
  TREQUEST_BULK_COMPLETE_CHECKLIST,
  TOrderChecklistItem,
} from '@monorepo/types';
import { TFilterGroupModel, TUserPaginationOption } from '@monorepo/ui';
import { select, Store } from '@ngrx/store';
import { TPlani8State } from '../reducers';
import * as orderChecklistActions from './order-checklist.action';
import * as orderChecklistSelectors from './order-checklist.selector';
import { AppNotificationUtilClass, Plani8DataService } from '@monorepo/services';
import { lastValueFrom, of } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';

export const ORDER_CHECKLIST_ITEMS_PER_PAGE_LIST = [10, 100, 150, 200];

@Injectable({ providedIn: 'root' })
export class OrderChecklistFacade {
  constructor(
    private store: Store<TPlani8State>,
    private appNotificationUtilClass: AppNotificationUtilClass,
    private plani8DataService: Plani8DataService,
  ) {}

  fetchOrderChecklist(payload: TREQUEST_ORDER_CHECKLIST_LISTING) {
    this.store.dispatch(new orderChecklistActions.GetOrderChecklist(payload));
  }

  getOrderChecklist() {
    return this.store.pipe(select(orderChecklistSelectors.getOrderChecklistInfo));
  }

  getUserPaginationOption() {
    return this.store.pipe(select(orderChecklistSelectors.getUserPaginationOption));
  }

  setUserPaginationOption(option: TUserPaginationOption) {
    return this.store.dispatch(new orderChecklistActions.SetUserPaginationOption(option));
  }

  setOrderChecklistFilterGroupModel(filterModel: TFilterGroupModel<TFilterSelected>) {
    this.store.dispatch(new orderChecklistActions.SetOrderChecklistSelectedFilters(filterModel));
  }

  getOrderChecklistFilterGroupModel() {
    return this.store.pipe(select(orderChecklistSelectors.getOrderChecklistSelectedFilters));
  }

  async submitOrderChecklist(payload: TREQUEST_SUBMIT_ORDER_CHECKLIST) {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    const dismiss = () => this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
    return lastValueFrom(
      this.plani8DataService.submitOrderChecklist(payload).pipe(
        map(() => true),
        catchError(() => of(false)),
        finalize(dismiss),
      ),
    );
  }

  async bulkCompleteChecklist(payload: TREQUEST_BULK_COMPLETE_CHECKLIST): Promise<boolean> {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    const dismiss = () => this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
    return lastValueFrom(
      this.plani8DataService.bulkCompleteChecklist(payload).pipe(
        map(() => true),
        catchError(() => of(false)),
        finalize(dismiss),
      ),
    );
  }

  refreshOrderChecklist() {
    this.store.dispatch(new orderChecklistActions.RefreshOrderChecklist());
  }

  async getOrderChecklistByOTL(otlId: string): Promise<TOrderChecklistItem | null> {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    const dismiss = () => this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
    return lastValueFrom(
      this.plani8DataService.getOrderChecklistByOTL(otlId).pipe(
        catchError(() => of(null)),
        finalize(dismiss),
      ),
    );
  }
}

export type TGetOrderChecklistInfo = ReturnType<OrderChecklistFacade['getOrderChecklist']>;
